import { useSelector } from "react-redux";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import InfoIcon from "@Icons/InfoIcon";
import { AppState } from "@Store/index";
import { CommunityData } from "../types";
import { resolveCopy } from "../utils";
import { factStatement, renderFactText } from "../utils/factDisplay";
import renderBoldText from "../utils/renderBoldText";
import renderRichText from "../utils/renderRichText";
import NeighbourhoodCard from "./NeighbourhoodCard";
import CARD_ICONS from "./cardAssets";
import styles from "../neighbourhoodSection.module.scss";

type CommunityTabProps = {
  data: CommunityData;
};

const KEY_PREFIX = "propertypage.components.neighbourhood.community";

const CommunityTab = ({ data }: CommunityTabProps) => {
  const { t } = useTranslationHook();

  // Card footer: the first entry of the "Universities" tab. Absent → the footer
  // strip and its divider are not rendered (P1D-5482).
  const nearestUniversity = useSelector((state: AppState) => {
    const tabs = state.propertyPageData?.nearbyLocations;
    if (!Array.isArray(tabs)) return null;
    const universityTab = tabs.find((tab: any) => tab?.name === "Universities");
    const first = universityTab?.items?.[0];
    if (!first?.name) return null;
    return { name: String(first.name), duration: first.duration ? String(first.duration) : "" };
  });

  // P1D-5540: with backend sentences, every data-driven string comes from the API.
  const { backend } = data;
  const source = backend?.source ?? resolveCopy(t, data.sourceKey);
  const sourceLabel = resolveCopy(t, "propertypage.components.neighbourhood.source.label");
  const noiseBanner = backend
    ? backend.banner && renderRichText(backend.banner)
    : data.noiseLevel &&
      renderBoldText(resolveCopy(t, `${KEY_PREFIX}.banner.noise.${data.noiseLevel}`));

  const legacyStat =
    data.mainStat && data.statKind
      ? resolveCopy(t, `${KEY_PREFIX}.card.stat.${data.statKind}`, { percent: data.mainStat })
      : data.mainStat;
  const legacySubtext = data.statKind
    ? resolveCopy(t, `${KEY_PREFIX}.card.subtext.${data.statKind}`)
    : undefined;
  const cardStat = backend ? backend.card.stat : legacyStat;
  const cardSubtext = backend ? backend.card.subtext : legacySubtext;

  // The API sends no Community footer (P1D-5540), so both modes read the nearest
  // university from nearbyLocations, as before.
  // Stacked: the label sits on its own line, the name below it,
  // truncated on that line while the duration stays put.
  const footer = nearestUniversity ? (
    <span className={styles.cardFooterStacked}>
      <span>{resolveCopy(t, `${KEY_PREFIX}.footer.nearestUniversity`)}</span>
      <span className={styles.cardFooterValueGroup}>
        <span className={styles.cardFooterName}>{nearestUniversity.name}</span>
        {nearestUniversity.duration && (
          <span className={styles.cardFooterMeta}>&middot; {nearestUniversity.duration}</span>
        )}
      </span>
    </span>
  ) : undefined;

  // Rows whose copy resolves to nothing are dropped along with their divider
  // (P1D-5482): a missing data point leaves no gap and no stray rule.
  const renderedFacts = data.facts
    .map((fact) => ({ fact, statement: factStatement(t, fact) }))
    .filter(({ statement }) => !!statement);

  return (
    <div className={styles.contentArea}>
      <NeighbourhoodCard
        image={data.heroImage}
        icon={CARD_ICONS.community}
        label={resolveCopy(t, `${KEY_PREFIX}.card.label`)}
        stat={cardStat}
        subtext={cardSubtext}
        alt="Community neighbourhood view"
        footer={footer}
      />
      <div className={styles.detailsSection}>
        <div>
          {renderedFacts.map(({ fact, statement }, index) => (
            <div key={fact.key}>
              <div
                className={formatClassNames(styles.tableRow, index === 0 && styles.tableRowFirst)}
              >
                <span className={styles.tableText}>{renderFactText(fact, statement)}</span>
              </div>
              {index < renderedFacts.length - 1 && <div className={styles.tableDivider} />}
            </div>
          ))}

          {noiseBanner && (
            <div className={styles.noiseBanner}>
              <span className={styles.noiseBannerText}>{noiseBanner}</span>
            </div>
          )}
        </div>
        <p className={styles.sourceText}>
          <span className={styles.sourceIcon} aria-hidden="true">
            <InfoIcon width="14" height="14" fill="#6b7280" />
          </span>
          {sourceLabel} : {source}
        </p>
      </div>
    </div>
  );
};

export default wrapperHOC(CommunityTab, {
  componentName: "CommunityTab",
  showForChina: false,
  showForWhiteLable: false,
});
